import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import { FieldShell, fieldBaseProps } from "./Fields";

/**
 * 檔案上傳（design system primitive，模板 Forms > FileUpload 的 dropzone）。
 *
 * 拖放或點選皆可,選完列出檔名與大小、可逐一移除。
 * 樣式沿用模板的 `.dropzone` / `.dz-message`(scss/custom/plugins/_form-upload.scss)。
 *
 *   <FileUploadField label="資產附件" files={files} onChange={setFiles} multiple />
 *
 * 受控:檔案清單由呼叫端持有(送出表單時才真的上傳)。
 */

/** 位元組轉人看得懂的大小(模板每頁各自複製一份,這裡收成一份) */
export function formatBytes(bytes, decimals = 2) {
  if (!bytes) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(decimals))} ${sizes[i]}`;
}

// dropzone 的 accept 物件({ mime: [".ext"] })轉原生 accept 屬性字串
const acceptAttrOf = (accept) =>
  accept
    ? Object.entries(accept).flatMap(([mime, exts]) => [mime, ...(exts || [])]).join(",")
    : undefined;

export const FileUploadField = ({
  label, name, files = [], onChange, required, error, help, disabled,
  multiple = true, accept, maxSize,
  hint = "把檔案拖進來,或點一下選擇檔案",
  compact = false,
}) => {
  const [rejected, setRejected] = useState([]);
  const nativeRef = useRef(null);

  const onDrop = (accepted, refused) => {
    setRejected((refused || []).map((r) => r.file.name));
    if (!onChange || !accepted.length) return;
    onChange(multiple ? [...files, ...accepted] : accepted.slice(0, 1));
  };
  const removeAt = (idx) => onChange && onChange(files.filter((_, i) => i !== idx));

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, multiple, disabled, ...(accept ? { accept } : {}), ...(maxSize ? { maxSize } : {}),
  });

  /* compact 版改用原生 input(見下);從清單移除到清空時,原生輸入框上的
     檔名顯示要跟著清,否則畫面說有檔、清單說沒有(檔案 input 只能設空值) */
  useEffect(() => {
    if (compact && files.length === 0 && nativeRef.current) nativeRef.current.value = "";
  }, [compact, files.length]);

  // 原生 file input 的 onChange:沿用 onDrop 的受控邏輯,maxSize 自己驗
  const onNativePick = (e) => {
    const picked = [...(e.target.files || [])];
    const refused = maxSize ? picked.filter((f) => f.size > maxSize) : [];
    onDrop(picked.filter((f) => !refused.includes(f)), refused.map((f) => ({ file: f })));
  };

  return (
    <FieldShell label={label} required={required} error={error} help={help}>
      {compact ? (
        /* UAT 8/27 客戶指定:表單內的上傳改用模板 /form-elements 的
           「Default file input」樣式(原生 <input type="file" class="form-control">,
           會顯示「選擇檔案｜未選擇任何檔案」)。dropzone 版保留給批次匯入
           (那頁設計稿本來就是 dropzone)。 */
        <input
          ref={nativeRef}
          type="file"
          name={name}
          className="form-control"
          disabled={disabled}
          multiple={multiple}
          accept={acceptAttrOf(accept)}
          onChange={onNativePick}
        />
      ) : (
      <div className={`dropzone${isDragActive ? " bg-light" : ""}`} style={{ minHeight: 140 }}>
        <div className="dz-message needsclick text-center py-4" {...getRootProps()}>
          <input {...getInputProps({ name })} />
          <i className="display-6 text-muted bx bx-cloud-upload" />
          <p className="mb-0 mt-2 fs-6">{hint}</p>
        </div>
      </div>
      )}

      {rejected.length ? (
        <div className="text-danger small mt-2">
          這些檔案不符合限制(格式或大小),已略過:{rejected.join("、")}
        </div>
      ) : null}

      {files.length ? (
        <ul className="list-unstyled mt-2 mb-0">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="d-flex align-items-center gap-2 border rounded px-2 py-1 mb-1">
              <i className="mdi mdi-file-outline text-muted" />
              <span className="flex-grow-1 text-truncate">{f.name}</span>
              <span className="text-muted small">{formatBytes(f.size)}</span>
              <button
                type="button"
                className="btn btn-sm btn-link text-danger p-0"
                aria-label={`移除 ${f.name}`}
                disabled={disabled}
                onClick={() => removeAt(i)}
              >
                <i className="mdi mdi-close" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </FieldShell>
  );
};
// 這個元件的值是 files 不是 value,所以不整包沿用 fieldBaseProps
const { value: _value, ...uploadBaseProps } = fieldBaseProps;
FileUploadField.propTypes = {
  ...uploadBaseProps,
  files: PropTypes.array,
  multiple: PropTypes.bool,
  accept: PropTypes.object,
  maxSize: PropTypes.number,
  hint: PropTypes.string,
  compact: PropTypes.bool,
};

export default FileUploadField;
